import React from "react";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { useTitle } from "hooks/graphql/useTitles";
import { triggerHaptic } from "@/utilities/haptics";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useRemoveSelection } from "@/features/betslip/hooks/useBetslipActions";
import type { RaceSelection } from "@/features/betslip/types";
import { BetCard } from "@/features/betslip/components/cards/BetCard";
import { SingleFooter } from "@/features/betslip/components/cards/SingleFooter";
import { SelectionAlert } from "@/features/betslip/components/parts/SelectionAlert";
import { SelectionEventStatus } from "@/features/betslip/components/parts/SelectionEventStatus";
import { BetCardHeader } from "@/features/betslip/components/parts/BetCardHeader";
import { BetRow } from "@/features/betslip/components/parts/BetRow";
import { BetSpine } from "@/features/betslip/components/parts/BetSpine";
import { RemoveButton } from "@/features/betslip/components/parts/RemoveButton";
import { PromoPills } from "@/features/betslip/components/parts/tokens/PromoPills";

type RaceSelectionCardProps = {
  outcomeId: string;
  selection: RaceSelection;
};

export const RaceSelectionCard = ({
  outcomeId,
  selection,
}: RaceSelectionCardProps) => {
  const title = useTitle((selection.sport ?? "").toLowerCase());
  const isPlacing = useBetslipStore((s) => s.isPlacing);
  const removeSelection = useRemoveSelection();

  const handleRemove = () => {
    triggerHaptic.click();
    removeSelection(outcomeId);
  };

  return (
    <BetCard
      testId={`selection-${outcomeId}`}
      alert={<SelectionAlert outcomeId={outcomeId} placement="card" />}
    >
      <BetCardHeader
        icon={<TitleIcon title={title} />}
        title={selection.eventName}
        clamp={<SelectionEventStatus selection={selection} />}
        rightSlot={
          <RemoveButton
            onClick={handleRemove}
            disabled={isPlacing}
            testId={`remove-${outcomeId}`}
          />
        }
        testId={`header-single-${outcomeId}`}
      />
      <BetSpine>
        <SelectionAlert outcomeId={outcomeId} placement="outcome" />
        <BetRow
          icon={
            <OutcomeIcon
              competitor={selection.competitor}
              silksUrl={selection.silksUrl}
              size={24}
              reserveAvatarSpace
            />
          }
          name={selection.selectionName}
          market={selection.marketName}
          testId={`bet-row-${outcomeId}`}
        />
      </BetSpine>
      <SingleFooter outcomeId={outcomeId} selection={selection} />
      <PromoPills
        outcomeIds={[outcomeId]}
        isMulti={false}
        selection={selection}
      />
    </BetCard>
  );
};
